import { useLazyQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { isRequired } from "@/lib/field-util.ts";
import type { FieldDef } from "@/lib/field-util.ts";
import { SuggestActivityDescriptionsDocument } from "@/graphql/generated/operations.ts";
import { Button } from "./Button";
import { DegreeSelect } from "./DegreeSelect";
import { MonthYearSelect } from "./MonthYearSelect";
import { Select } from "./Select";
import { SkillLevelScale } from "./SkillLevelScale";

// The single form mask used both when adding (AddForm) and editing existing
// entries. Same field definitions, same visual treatment - fields are defined
// once and reused on both paths.

export function RequiredBadge() {
  return (
    <span className="smycles-mandatory">
      <FormattedMessage id="design.requiredField" defaultMessage="Required field" />
    </span>
  );
}

export type EntryFieldValues = Record<string, string | number | boolean | undefined>;

interface EntryFieldsProps {
  fields: readonly FieldDef[];
  values: EntryFieldValues;
  onChange: (key: string, value: string | boolean) => void;
  autoFocus?: boolean;
}

export function EntryFields({ fields, values, onChange, autoFocus = true }: EntryFieldsProps) {
  // Walk the field list and group consecutive paired fields. A field with
  // `pairWithNext: true` is rendered next to fields[i+1] inside a 2-col grid
  // (stacked on narrow viewports, side-by-side from sm:). All other fields
  // render full-width as before.
  const rows: Array<{ kind: "single"; field: FieldDef; index: number } | { kind: "pair"; a: FieldDef; aIndex: number; b: FieldDef; bIndex: number }> = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (!f) continue;
    const next = fields[i + 1];
    if (f.pairWithNext && next) {
      rows.push({ kind: "pair", a: f, aIndex: i, b: next, bIndex: i + 1 });
      i++;
    } else {
      rows.push({ kind: "single", field: f, index: i });
    }
  }

  return (
    <div className="space-y-3">
      {rows.map((row) =>
        row.kind === "single" ? (
          <FieldRow
            key={row.field.key}
            field={row.field}
            values={values}
            onChange={onChange}
            isFirst={row.index === 0 && autoFocus}
          />
        ) : (
          <div key={`${row.a.key}+${row.b.key}`} className="grid grid-cols-1 gap-3 @sm:grid-cols-2">
            <FieldRow
              field={row.a}
              values={values}
              onChange={onChange}
              isFirst={row.aIndex === 0 && autoFocus}
            />
            <FieldRow
              field={row.b}
              values={values}
              onChange={onChange}
              isFirst={false}
            />
          </div>
        ),
      )}
    </div>
  );
}

function FieldRow({
  field: f,
  values,
  onChange,
  isFirst,
}: {
  field: FieldDef;
  values: EntryFieldValues;
  onChange: (key: string, value: string | boolean) => void;
  isFirst: boolean;
}) {
  const intl = useIntl();
  const req = isRequired(f);
  const reqRing = req ? "ring-accent-decoration/60" : "ring-ink/10";
  const raw = values?.[f.key];
  const v = raw == null ? "" : String(raw);

  return (
    <div>
      {!f.hideLabel ? (
        <div className="smycles-field-label mb-1">
          {f.label}
          {req ? <RequiredBadge /> : null}
        </div>
      ) : null}
      {f.type === "string-list" ? (
        <StringListField
          id={f.id}
          value={v}
          placeholder={f.placeholder}
          reqRing={reqRing}
          onChange={(val) => onChange(f.key, val)}
          suggest={f.suggest}
          maxEntries={f.maxEntries}
          jobTitle={values?.jobTitle == null ? "" : String(values.jobTitle)}
          department={values?.department == null ? "" : String(values.department)}
        />
      ) : f.type === "select" ? (
        f.selectKind === "degree" ? (
          <DegreeSelect
            id={f.id}
            value={v}
            onChange={(val) => onChange(f.key, val)}
            options={f.options ?? []}
            placeholder={f.placeholder}
            required={req}
          />
        ) : (
          <Select
            id={f.id}
            value={v}
            onChange={(val) => onChange(f.key, val)}
            options={f.options ?? []}
            placeholder={f.placeholder ?? "–"}
            required={req}
            className={req ? "ring-accent-decoration/60" : ""}
          />
        )
      ) : f.type === "skill" ? (
        <SkillLevelScale
          idPrefix={f.id}
          value={Number(v) || 0}
          onChange={(p) => onChange(f.key, String(p))}
        />
      ) : f.type === "month-year" ? (
        <MonthYearSelect
          id={f.id}
          value={v}
          onChange={(val) => onChange(f.key, val)}
          accent={req}
        />
      ) : f.type === "bool" ? (
        <label className="smycles-field-value flex items-center gap-2 text-ink">
          <input
            id={f.id}
            type="checkbox"
            checked={raw === true}
            onChange={(e) => onChange(f.key, e.target.checked)}
          />
          {f.placeholder ?? intl.formatMessage({ id: "design.booleanYes", defaultMessage: "Yes" })}
        </label>
      ) : (
        // Text/date input with an inline × clear button. The button is hidden
        // by default and revealed on hover/focus of the wrapper; it sits over
        // the input's right edge so the input keeps its full width.
        <div className="group relative">
          <input
            id={f.id}
            autoFocus={isFirst}
            type={f.type === "date" ? "date" : "text"}
            value={v}
            placeholder={f.placeholder ?? ""}
            onChange={(e) => onChange(f.key, e.target.value)}
            className={
              f.prominent
                ? `w-full rounded-md bg-surface px-3 py-2.5 pr-9 text-smycles-title ring-1 ${reqRing} focus:outline-none focus:ring-1 focus:ring-ink-accent-ui`
                : `smycles-field-value w-full rounded-md bg-surface px-3 py-2.5 pr-9 ring-1 ${reqRing} focus:outline-none focus:ring-1 focus:ring-ink-accent-ui`
            }
          />
          {v && f.type !== "date" ? (
            <button
              type="button"
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(f.key, "");
              }}
              aria-label={intl.formatMessage({ id: "design.clearValue", defaultMessage: "Clear value" })}
              title={intl.formatMessage({ id: "design.clearValue", defaultMessage: "Clear value" })}
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-smycles-title leading-none text-ink-muted opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100 hover:bg-ink/10 hover:text-ink"
            >
              × {/* i18n-allow */}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

// A list of strings edited as one input row per item, persisted as a single
// '\n'-joined string (one line = one list entry). Conversion back to [String!]
// happens at the mutation boundary (profile-entries linesToList).
function StringListField({
  id,
  value,
  placeholder,
  reqRing,
  onChange,
  suggest,
  maxEntries,
  jobTitle,
  department,
}: {
  id?: string;
  value: string;
  placeholder?: string;
  reqRing: string;
  onChange: (value: string) => void;
  suggest?: "activity";
  maxEntries?: number;
  jobTitle?: string;
  department?: string;
}) {
  const intl = useIntl();
  const lines = value === "" ? [""] : value.split("\n");
  const commit = (next: string[]) => onChange((next.length ? next : [""]).join("\n"));
  // Blank lines are dropped on commit, so they must not count towards the cap either — otherwise a
  // freshly added empty row would consume one of the user's slots.
  const filled = lines.filter((l) => l.trim() !== "");
  const atCap = maxEntries != null && filled.length >= maxEntries;
  const addSuggestion = (text: string) => {
    if (atCap) return;
    commit([...filled, text]);
  };

  const [fetchSuggestions, { data, loading, called }] = useLazyQuery(SuggestActivityDescriptionsDocument, {
    fetchPolicy: "no-cache",
  });
  const canSuggest = suggest === "activity" && (jobTitle ?? "").trim() !== "";
  const run = () =>
    fetchSuggestions({
      variables: {
        input: {
          locale: intl.locale,
          jobTitle: (jobTitle ?? "").trim(),
          ...((department ?? "").trim() ? { department: (department ?? "").trim() } : {}),
        },
      },
    });

  const result = data?.suggestActivityDescriptions;
  const taken = new Set(lines.map((l) => l.trim().toLowerCase()).filter(Boolean));
  const fresh = (result?.suggestions ?? []).filter((s) => !taken.has(s.trim().toLowerCase()));

  return (
    <div id={id} className="space-y-2">
      {lines.map((line, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            // Zeilennummer statt Inhalt: die Zeile ist über ihre Position
            // adressierbar, bevor sie überhaupt etwas enthält.
            id={id && `${id}-${i}`}
            type="text"
            value={line}
            placeholder={placeholder ?? ""}
            onChange={(e) => commit(lines.map((l, idx) => (idx === i ? e.target.value : l)))}
            className={`smycles-field-value w-full rounded-md bg-surface px-3 py-2.5 ring-1 ${reqRing} focus:outline-none focus:ring-1 focus:ring-ink-accent-ui`}
          />
          <button
            id={id && `${id}-${i}-remove`}
            type="button"
            onClick={() => commit(lines.filter((_, idx) => idx !== i))}
            aria-label={intl.formatMessage({ id: "design.removeTask", defaultMessage: "Remove task" })}
            className="shrink-0 rounded-md px-2 py-1 text-ink-muted ring-1 ring-ink/10 hover:text-ink-accent hover:ring-accent-decoration"
          >
            ✕
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          id={id && `${id}-add`}
          variant="chip"
          size="sm"
          disabled={atCap}
          onClick={() => commit([...lines, ""])}
        >
          <FormattedMessage id="suggestions.addTask" defaultMessage="+ Add task" />
        </Button>
        {maxEntries != null ? (
          <span className="smycles-field-meta text-ink-muted" data-testid="string-list-counter">
            <FormattedMessage
              id="profile.page.field.activities.counter"
              defaultMessage="{count} of {max}"
              values={{ count: filled.length, max: maxEntries }}
            />
          </span>
        ) : null}
        {suggest === "activity" ? (
          <Button
            variant="chip"
            size="sm"
            disabled={!canSuggest || loading || atCap}
            onClick={run}
            title={
              canSuggest
                ? undefined
                : intl.formatMessage({ id: "suggestions.needJobTitle", defaultMessage: "Erst Stellenbezeichnung eingeben" })
            }
            className="smycles-ai-shadow disabled:opacity-40"
          >
            {loading ? (
              <FormattedMessage id="suggestions.loading" defaultMessage="Vorschläge werden geladen…" />
            ) : (
              <FormattedMessage id="suggestions.activities" defaultMessage="Aufgaben vorschlagen" />
            )}
          </Button>
        ) : null}
      </div>

      {suggest === "activity" && called && !loading ? (
        <div className="smycles-ai-shadow box-flat space-y-2 p-3">
          {result?.status === "OK" && fresh.length > 0 ? (
            <>
              <div className="smycles-field-meta text-ink-muted">
                <FormattedMessage id="suggestions.add" defaultMessage="Tippen zum Hinzufügen" />
              </div>
              <div className="flex flex-wrap gap-2">
                {fresh.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addSuggestion(s)}
                    className="rounded-full bg-ink/[0.06] px-3 py-1 text-smycles-label text-ink ring-1 ring-ink/10 hover:text-ink-accent hover:ring-accent-decoration"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            </>
          ) : result?.status === "UNAVAILABLE" ? (
            <div className="flex items-center gap-2">
              <span className="smycles-field-meta text-ink-muted">
                <FormattedMessage id="suggestions.unavailable" defaultMessage="Vorschläge derzeit nicht verfügbar" />
              </span>
              <button type="button" onClick={run} className="smycles-field-meta text-ink-accent hover:underline">
                <FormattedMessage id="suggestions.retry" defaultMessage="Erneut versuchen" />
              </button>
            </div>
          ) : (
            <div className="smycles-field-meta text-ink-muted">
              <FormattedMessage id="suggestions.empty" defaultMessage="Keine Vorschläge gefunden" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
